import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "@iconify/react";
import apiClient from "../../store/services/apiClient";
import sanitizeRichTextHtml from "../../utils/sanitizeRichTextHtml";
import styles from "./NoPromotionRewardTab.module.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.pgzone88.com"
).replace(/\/+$/, "");

const buildImageUrl = (value = "") => {
  const path = String(value || "").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const AUTO_SLIDE_MS = 6000;

const NoPromotionRewardTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [popupList, setPopupList] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideDir, setSlideDir] = useState("right");
  const dirRef = useRef("right");

  const loadPopup = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.get(
        "/setting/popup/no-promotion-reward?active=1",
      );
      setPopupList(response.data?.data || []);
    } catch {
      setError("ไม่สามารถโหลดข้อมูลไม่รับโปรโมชั่นได้");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPopup();
  }, [loadPopup]);

  const slides = useMemo(
    () =>
      (popupList || [])
        .filter((item) => item?.popupContent)
        .map((item) => ({
          ...item,
          imageUrl: buildImageUrl(item?.imageUrl),
          safeHtml: sanitizeRichTextHtml(item?.popupContent || ""),
        })),
    [popupList],
  );

  useEffect(() => {
    if (!slides.length) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((cur) => (cur >= slides.length ? 0 : cur));
  }, [slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const id = window.setInterval(() => {
      dirRef.current = "right";
      setSlideDir("right");
      setActiveIndex((cur) => (cur + 1) % slides.length);
    }, AUTO_SLIDE_MS);
    return () => window.clearInterval(id);
  }, [slides.length]);

  const activeSlide = slides[activeIndex] || null;

  const goToSlide = useCallback(
    (nextIndex) => {
      if (!slides.length) return;
      const normalized = (nextIndex + slides.length) % slides.length;
      const dir =
        normalized === (activeIndex + 1) % slides.length ||
        (activeIndex === slides.length - 1 && normalized === 0)
          ? "right"
          : "left";
      dirRef.current = dir;
      setSlideDir(dir);
      setActiveIndex(normalized);
    },
    [slides.length, activeIndex],
  );

  if (loading) {
    return (
      <div className={styles.stateBox}>
        <div>
          <div className={styles.spinner} />
          <div>กำลังโหลดข้อมูล...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.stateBox}>
        <div>
          <div>{error}</div>
          <button type="button" className={styles.retryBtn} onClick={loadPopup}>
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  if (!activeSlide) {
    return (
      <div className={styles.stateBox}>
        <div>
          <Icon icon="fluent-color:gift-card-24" width={52} />
          <div style={{ marginTop: 12 }}>
            ยังไม่มีข้อมูลไม่รับโปรโมชั่นจากระบบ admin
          </div>
        </div>
      </div>
    );
  }

  const slideAnim =
    slideDir === "left" ? styles.slideFromLeft : styles.slideFromRight;

  return (
    <div className={styles.container}>
      {/* Main popup slide card */}
      <div className={styles.slideStage}>
        <div key={activeIndex} className={`${styles.slide} ${slideAnim}`}>
          {/* Image panel */}
          <div className={styles.imagePanel}>
            {activeSlide.imageUrl ? (
              <img
                src={activeSlide.imageUrl}
                alt={activeSlide.popupName}
                className={styles.slideImg}
              />
            ) : (
              <div className={styles.imgFallback}>
                <Icon icon="fluent-color:gift-card-24" width={72} />
              </div>
            )}
          </div>

          {/* Content panel */}
          <div className={styles.contentPanel}>
            <div className={styles.eyebrow}>สิทธิพิเศษสำหรับคุณ</div>
            <h3 className={styles.slideTitle}>{activeSlide.popupName}</h3>

            <div
              className={styles.slideBody}
              dangerouslySetInnerHTML={{ __html: activeSlide.safeHtml }}
            />

            {/* Nav row */}
            <div className={styles.navRow}>
              {slides.length > 1 ? (
                <>
                  <button
                    type="button"
                    className={styles.navBtn}
                    onClick={() => goToSlide(activeIndex - 1)}
                    aria-label="ก่อนหน้า"
                  >
                    <Icon icon="solar:alt-arrow-left-bold" width={16} />
                  </button>

                  <div className={styles.dots}>
                    {slides.map((item, i) => (
                      <button
                        key={item.id || i}
                        type="button"
                        className={`${styles.dot} ${
                          i === activeIndex ? styles.dotActive : ""
                        }`}
                        onClick={() => goToSlide(i)}
                        aria-label={`สไลด์ ${i + 1}`}
                      />
                    ))}
                  </div>

                  <button
                    type="button"
                    className={styles.navBtn}
                    onClick={() => goToSlide(activeIndex + 1)}
                    aria-label="ถัดไป"
                  >
                    <Icon icon="solar:alt-arrow-right-bold" width={16} />
                  </button>
                </>
              ) : null}

              <span className={styles.counter}>
                <span className={styles.counterCurrent}>
                  {String(activeIndex + 1).padStart(2, "0")}
                </span>
                <span className={styles.counterSep}>/</span>
                {String(slides.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail strip */}
      {slides.length > 1 ? (
        <div className={styles.thumbStrip}>
          {slides.map((item, i) => (
            <button
              key={item.id || i}
              type="button"
              className={`${styles.thumbCard} ${
                i === activeIndex ? styles.thumbCardActive : ""
              }`}
              onClick={() => goToSlide(i)}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.popupName}
                  className={styles.thumbImg}
                />
              ) : (
                <div className={styles.thumbFallback}>
                  <Icon icon="fluent-color:gift-card-24" width={28} />
                </div>
              )}
              <div className={styles.thumbName}>{item.popupName}</div>
              {i === activeIndex ? (
                <div className={styles.thumbActiveLine} />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default NoPromotionRewardTab;
